import React, { useState, useEffect } from 'react';
import { Users, CheckCircle, XCircle, Clock, Search } from 'lucide-react';
import { agenciesAPI } from '../utils/api';
import constant from '../constants/constant';

const AgencyHosts = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState({ message: '', type: '', visible: false });

  useEffect(() => {
    fetchApplications();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: '', type: '', visible: false }), 3000);
  };

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const res = await agenciesAPI.getHostApplications();
      setApplications(res.data || []);
    } catch (err) {
      console.error('Error fetching host applications:', err);
      showToast('Error loading host applications', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    if (window.confirm(`Are you sure you want to ${status} this application?`)) {
      try {
        await agenciesAPI.updateHostApplication(id, status);
        showToast(`Application ${status} successfully`);
        fetchApplications();
      } catch (err) {
        showToast(err.response?.data?.message || 'Error updating application status', 'error');
      }
    }
  };

  const filteredApps = applications.filter(app => 
    app.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h2 className="text-4xl font-black text-white tracking-tight">Host Management</h2>
          <p className="text-slate-400 mt-2 font-medium">Review and manage your agency's host applications.</p>
        </div>
        
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
          <input
            type="text"
            placeholder="Search hosts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full md:w-80 bg-[#0f172a] border border-white/10 text-white pl-12 pr-4 py-4 rounded-2xl focus:outline-none focus:border-pink-500 transition-colors font-medium"
          />
        </div>
      </div>

      {/* Applications List */}
      <div className="bg-[#0f172a] border border-white/10 rounded-[32px] overflow-hidden">
        {loading ? (
          <div className="p-20 text-center animate-pulse">
            <div className="w-16 h-16 border-4 border-pink-500/20 border-t-pink-500 rounded-full animate-spin mx-auto mb-6"></div>
            <p className="text-slate-400 font-bold tracking-widest uppercase">Loading Applications...</p>
          </div>
        ) : filteredApps.length === 0 ? (
          <div className="p-20 text-center">
            <Users className="mx-auto mb-6 text-slate-700 w-20 h-20" />
            <p className="text-slate-400 font-bold text-lg">No host applications found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="px-8 py-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Host</th>
                  <th className="px-8 py-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Email</th>
                  <th className="px-8 py-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Date Applied</th>
                  <th className="px-8 py-6 text-xs font-bold text-slate-500 uppercase tracking-widest">Status</th>
                  <th className="px-8 py-6 text-right text-xs font-bold text-slate-500 uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredApps.map((app) => (
                  <tr key={app.id} className="hover:bg-white/[0.02] transition-colors group">
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-4">
                        <img 
                          src={app.profile_image ? `${constant.BASE_URL}${app.profile_image}` : 'https://api.dicebear.com/7.x/avataaars/svg?seed=' + app.first_name} 
                          alt="avatar" 
                          className="w-12 h-12 rounded-2xl object-cover bg-white/10"
                        />
                        <div>
                          <p className="font-bold text-white text-base">{app.first_name} {app.last_name}</p>
                          <p className="text-xs text-slate-500 font-medium">ID: {app.user_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <p className="text-slate-300 font-medium">{app.email}</p>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-2 text-slate-400">
                        <Clock size={16} />
                        <span className="font-medium">{new Date(app.created_at).toLocaleDateString()}</span>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wider
                        ${app.status === 'approved' ? 'bg-emerald-500/10 text-emerald-500' :
                          app.status === 'rejected' ? 'bg-rose-500/10 text-rose-500' :
                          'bg-amber-500/10 text-amber-500'}`}>
                        {app.status}
                      </span>
                    </td>
                    <td className="px-8 py-6 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {app.status === 'approved' && (
                          <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                            Approved
                          </span>
                        )}
                        {app.status === 'pending' && (
                          <div className="flex items-center justify-end gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleUpdateStatus(app.id, 'approved')}
                              className="p-3 bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500 hover:text-white rounded-xl transition-all"
                              title="Approve"
                            >
                              <CheckCircle size={20} />
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(app.id, 'rejected')}
                              className="p-3 bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white rounded-xl transition-all"
                              title="Reject"
                            >
                              <XCircle size={20} />
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed bottom-10 right-10 flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl animate-in slide-in-from-right-8 z-50
          ${toast.type === 'error' ? 'bg-rose-500 text-white' : 'bg-emerald-500 text-white'}`}>
          {toast.type === 'error' ? <XCircle size={24} /> : <CheckCircle size={24} />}
          <p className="font-bold">{toast.message}</p>
        </div>
      )}
    </div>
  );
};

export default AgencyHosts;
